import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { feedbackService } from '@/services/api/feedbackService'
import type { AnonymousFeedbackRequest } from '@/services/api/feedbackService'
import FeedbackResponseForm, { QuestionResponse } from '@/components/performance/feedback/FeedbackResponseForm'
import { questionsToAnswer } from '@/components/performance/feedback/questionSnapshot'
import { Loader2, CheckCircle, AlertCircle } from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import toast from 'react-hot-toast'

/** Returns context-appropriate privacy messaging based on request flags */
function getPrivacyInfo(req: AnonymousFeedbackRequest | null) {
  if (req?.is_anonymous) {
    return {
      headerTitle: 'Anonymous Feedback',
      headerSubtitle: 'Your feedback is anonymous and will not be attributed to you',
      privacyNote: 'Your feedback is anonymous and will not be attributed to you.',
      submittedNote: 'Your feedback has been submitted successfully and will not be attributed to you.',
      footerNote: 'Your feedback is anonymous and will not be attributed to you',
    }
  }
  if (req?.is_confidential) {
    return {
      headerTitle: 'Confidential Feedback',
      headerSubtitle: 'Your responses are confidential — your name will not be shown alongside your feedback',
      privacyNote: 'Your responses are confidential — your name will not be shown alongside your feedback.',
      submittedNote: 'Your feedback has been submitted successfully. Your name will not be shown alongside your responses.',
      footerNote: 'Your responses are confidential — your name will not be shown alongside your feedback',
    }
  }
  return {
    headerTitle: 'Feedback Request',
    headerSubtitle: 'Thank you for taking the time to share your feedback',
    privacyNote: 'Thank you for your participation!',
    submittedNote: 'Your feedback has been submitted successfully.',
    footerNote: 'Thank you for taking the time to share your feedback',
  }
}

export default function AnonymousFeedbackResponsePage() {
  const { token } = useParams<{ token: string }>()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [alreadyResponded, setAlreadyResponded] = useState(false)
  const [request, setRequest] = useState<AnonymousFeedbackRequest | null>(null)
  const [employeeName, setEmployeeName] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const privacy = getPrivacyInfo(request)

  useEffect(() => {
    if (token) {
      loadRequestByToken()
    } else {
      setError('Invalid feedback link')
      setLoading(false)
    }
  }, [token])

  const loadRequestByToken = async () => {
    try {
      setLoading(true)
      setError(null)

      if (!token) {
        throw new Error('No token provided')
      }

      const data = await feedbackService.getAnonymousFeedbackRequest(token)

      // The requester's final list when the request carries one; otherwise the
      // template's questions (older requests and campaigns), as before.
      data.custom_questions = questionsToAnswer(data)

      setRequest(data)

      // Check if this feedback was already submitted
      if (data.already_responded) {
        setAlreadyResponded(true)
      }

      // Use employee name from the anonymous response (populated by backend)
      // This avoids making an authenticated API call on a public page. The
      // link never carries a personnel number (Oct 2026): when the name is
      // unknown, the page says "the Team Member".
      setEmployeeName(data.requested_for_user_name || 'the Team Member')
    } catch (err: any) {
      console.error('Failed to load feedback request:', err)
      setError(err.message || 'Failed to load feedback request')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (
    responseData: QuestionResponse[],
    overallRating?: number,
    generalComments?: string
  ) => {
    if (!token) return

    try {
      setSubmitting(true)

      await feedbackService.submitAnonymousFeedback({
        anonymity_token: token,
        response_data: responseData,
        overall_rating: overallRating,
        general_comments: generalComments
      })

      setSubmitted(true)
      toast.success('Thank you! Your feedback has been submitted.')
    } catch (err: any) {
      console.error('Failed to submit feedback:', err)
      toast.error(err.response?.data?.detail || 'Failed to submit feedback')
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heritage-blue via-future-blue to-heritage-blue flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-3x pt-3x">
            <div className="flex flex-col items-center justify-center space-y-4">
              <img
                src="/assets/logos/eduvos-horizontal-white.png"
                alt="Eduvos"
                className="h-12 mb-2x opacity-0"
              />
              <Loader2 className="h-12 w-12 animate-spin text-heritage-blue" />
              <p className="font-secondary text-heritage-blue/70">Loading feedback request...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heritage-blue via-future-blue to-heritage-blue flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-3x pt-3x">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <img
                src="/assets/logos/eduvos-horizontal-white.png"
                alt="Eduvos"
                className="h-12 mb-2x opacity-0"
              />
              <AlertCircle className="h-16 w-16 text-red-600" />
              <h2 className="font-primary text-2xl font-bold text-heritage-blue">Invalid Link</h2>
              <p className="font-secondary text-heritage-blue/70">{error}</p>
              <p className="font-secondary text-sm text-heritage-blue/60">
                Please check your email for the correct feedback link or contact the person who sent you the request.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (alreadyResponded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heritage-blue via-future-blue to-heritage-blue flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-3x pt-3x">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <img
                src="/assets/logos/eduvos-horizontal-white.png"
                alt="Eduvos"
                className="h-12 mb-2x opacity-0"
              />
              <CheckCircle className="h-16 w-16 text-green-600" />
              <h2 className="font-primary text-2xl font-bold text-heritage-blue">Already Submitted</h2>
              <p className="font-secondary text-heritage-blue/70">
                You have already submitted your feedback for this request. Thank you for your participation!
              </p>
              <p className="font-secondary text-sm text-heritage-blue/60">
                {privacy.privacyNote}
              </p>
              <p className="font-secondary text-xs text-heritage-blue/50 mt-2x">
                You may close this tab.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-heritage-blue via-future-blue to-heritage-blue flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-3x pt-3x">
            <div className="flex flex-col items-center justify-center space-y-4 text-center">
              <img
                src="/assets/logos/eduvos-horizontal-white.png"
                alt="Eduvos"
                className="h-12 mb-2x opacity-0"
              />
              <CheckCircle className="h-16 w-16 text-green-600" />
              <h2 className="font-primary text-2xl font-bold text-heritage-blue">Thank You!</h2>
              <p className="font-secondary text-heritage-blue/70">
                Your feedback is valuable and will contribute to {employeeName || 'the team member'}'s impact & growth journey.
              </p>
              <p className="font-secondary text-xs text-heritage-blue/50 mt-2x">
                You may close this tab.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-heritage-blue via-future-blue to-heritage-blue">
      <div className="container mx-auto px-4 py-6 md:py-8">
        {/* Header - With 2X minimum clear space around logo per brand guidelines */}
        <div className="mb-3x bg-white/10 backdrop-blur-sm rounded-lg px-3x py-2x shadow-lg">
          <div className="flex items-center justify-center mb-2x">
            <img
              src="/assets/logos/eduvos-horizontal-white.png"
              alt="Eduvos"
              className="h-16 md:h-20"
            />
          </div>
          <div className="text-white text-center">
            <p className="font-primary text-lg font-semibold">{privacy.headerTitle}</p>
            <p className="font-secondary text-sm opacity-90">{privacy.headerSubtitle}</p>
          </div>
        </div>

        {/* Form Card */}
        <Card className="max-w-4xl mx-auto shadow-2xl">
          <CardContent className="p-3x pt-3x">
            {request && employeeName ? (
              <FeedbackResponseForm
                requestId={request.id}
                requestTitle={
                  request.title && !request.title.includes('EMPLOYEE_REQUESTED') && !request.title.includes('MANAGER_INITIATED')
                    ? request.title
                    : `Feedback Request for ${employeeName}`
                }
                requestedForUser={employeeName}
                questions={request.custom_questions || []}
                isAnonymous={request.is_anonymous}
                isConfidential={request.is_confidential}
                instructions={request.instructions}
                onSubmit={handleSubmit}
                loading={submitting}
              />
            ) : request && !employeeName ? (
              <div className="text-center py-3x">
                <Loader2 className="h-8 w-8 animate-spin text-heritage-blue mx-auto" />
                <p className="font-secondary text-heritage-blue/70 mt-2x">Loading team member details...</p>
              </div>
            ) : (
              <div className="text-center py-3x">
                <p className="font-secondary text-heritage-blue/70">Feedback request not found</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-3x text-white">
          <p className="font-secondary text-sm opacity-75">© {new Date().getFullYear()} Eduvos (impact & growth)+ Platform</p>
          <p className="font-secondary text-xs opacity-60 mt-x">{privacy.footerNote}</p>
        </div>
      </div>
    </div>
  )
}
